import type { CaseDetailView } from '@concord/domain/contracts';
import { Button, Notice } from '@concord/ui';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useParams } from 'react-router';
import { api, errorMessage } from '../../lib/api';
import type { Me } from '../../lib/session';
import { ConsoleLayout } from '../console/ConsoleLayout';
import { AddRecordDialog } from './AddRecord';
import { CaseActions } from './CaseActions';
import {
  ConsultationPanel,
  GuestControls,
  InvitationControls,
  SetTimeDialog,
  StateBanner,
} from './CaseControls';
import { CaseScreen } from './CaseScreen';
import { RemovedItems } from './RemovedItems';

export const caseKey = (key: string) => ['case', key] as const;

export function useCase(key: string) {
  return useQuery({
    queryKey: caseKey(key),
    queryFn: () => api<CaseDetailView>(`/staff/cases/${encodeURIComponent(key)}`),
  });
}

/** /console/cases/:ref — the case screen inside the console frame, with its actions. */
export function CasePage({ me }: { me: Me }) {
  const { key = '' } = useParams();
  const q = useCase(key);
  const [settingTime, setSettingTime] = useState(false);
  const [addingRecord, setAddingRecord] = useState(false);
  const c = q.data;
  const tz = me.timezone;
  return (
    <ConsoleLayout me={me} current="cases">
      {q.isPending && <div className="skeleton" style={{ height: 420 }} />}
      {q.isError && <Notice tone="bad">{errorMessage(q.error)}</Notice>}
      {c && (
        <>
          <CaseScreen
            c={c}
            tz={tz}
            queueCrumb={{ label: c.stateLabel, to: '/console/cases' }}
            actions={c.state !== 'draft' && <CaseActions c={c} onSetTime={() => setSettingTime(true)} />}
            banner={<StateBanner c={c} tz={tz} onSetTime={() => setSettingTime(true)} />}
            consultation={<ConsultationPanel c={c} tz={tz} onSetTime={() => setSettingTime(true)} />}
            invitationControls={(i) => <InvitationControls c={c} i={i} tz={tz} />}
            guestControls={(g) => <GuestControls c={c} guestId={g.id} />}
            reveal={async (field, guestId) =>
              (
                await api<{ value: string | null }>(
                  `/staff/cases/${encodeURIComponent(key)}/reveal?field=${field}${guestId ? `&id=${guestId}` : ''}`,
                )
              ).value
            }
            recordsAction={
              !c.readOnly &&
              c.state !== 'draft' && (
                <Button variant="sec" onClick={() => setAddingRecord(true)} style={{ marginLeft: 'auto' }}>
                  Add a record
                </Button>
              )
            }
          />
          {me.permissions.includes('admin.audit') && c.reference && (
            <RemovedItems caseRef={c.reference} tz={tz} readOnly={c.readOnly} />
          )}
          {addingRecord && <AddRecordDialog c={c} onDone={() => setAddingRecord(false)} />}
          {settingTime && <SetTimeDialog c={c} tz={tz} open={settingTime} onOpenChange={setSettingTime} />}
        </>
      )}
    </ConsoleLayout>
  );
}
